import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConciergeWidget() {
    const { t, locale } = useLang();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([{ role: "assistant", text: t("concierge.welcome") }]);
    const [text, setText] = useState("");
    const [email, setEmail] = useState("");
    const [session, setSession] = useState(null);
    const [busy, setBusy] = useState(false);
    const [cookieVisible, setCookieVisible] = useState(false);
    const scrollRef = useRef(null);

    // Hide the floating pill while the cookie banner is on screen so the
    // two never visually overlap. CookieShield mounts its own node at
    // #eigoo-cookie-shield; we poll briefly on mount since the banner is
    // only present until the user accepts (then unmounted).
    useEffect(() => {
        const check = () => setCookieVisible(!!document.getElementById("eigoo-cookie-shield"));
        check();
        const id = setInterval(check, 500);
        return () => clearInterval(id);
    }, []);

    // Refresh the welcome message when language flips
    useEffect(() => {
        setMessages((m) => (m.length <= 1 ? [{ role: "assistant", text: t("concierge.welcome") }] : m));
    }, [locale, t]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, busy, open]);

    const send = async () => {
        const msg = text.trim();
        if (!msg) return;
        setMessages((m) => [...m, { role: "user", text: msg }]);
        setText("");
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/ai/concierge`, {
                message: msg,
                session_id: session || undefined,
                visitor_email: email || undefined,
                locale,
            });
            setSession(data.session_id);
            setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", text: "Sorry — my line to the studio hiccuped. Try again in a moment." }]);
        } finally { setBusy(false); }
    };

    return (
        <>
            {!open && !cookieVisible && (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed bottom-6 right-3 sm:bottom-6 sm:right-6 z-40 rounded-full shadow-xl hover:scale-105 transition-transform flex items-center gap-2 px-4 py-3 sm:px-5 sm:py-3.5 font-semibold text-forest-deep max-w-[calc(100vw-24px)]"
                    style={{ backgroundColor: "#c9a84c", paddingRight: "max(16px, env(safe-area-inset-right))" }}
                    data-testid="concierge-open-btn"
                    aria-label="Open Island Gold concierge"
                >
                    <Sparkles size={16} />
                    <span className="text-[0.72rem] tracking-[0.22em] uppercase whitespace-nowrap">{t("concierge.open")}</span>
                </button>
            )}
            {open && (
                <div
                    className="fixed bottom-6 right-3 sm:bottom-6 sm:right-6 z-40 w-[calc(100vw-24px)] max-w-sm h-[560px] max-h-[70vh] rounded-sm shadow-2xl flex flex-col overflow-hidden border border-black/10"
                    style={{ backgroundColor: "#161616" }}
                    data-testid="concierge-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ backgroundColor: "#0d0d0d" }}>
                        <div className="flex items-center gap-2">
                            <Sparkles size={14} style={{ color: "#c9a84c" }} />
                            <span className="text-[0.7rem] tracking-[0.3em] uppercase font-semibold" style={{ color: "#c9a84c" }}>
                                Island Gold Concierge
                            </span>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="text-white/60 hover:text-white"
                            aria-label="Close concierge"
                            data-testid="concierge-close-btn"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="concierge-messages">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] text-sm leading-relaxed px-3.5 py-2.5 rounded-sm whitespace-pre-wrap ${
                                        m.role === "user" ? "text-forest-deep" : "text-white/90"
                                    }`}
                                    style={{
                                        backgroundColor: m.role === "user" ? "#c9a84c" : "rgba(255,255,255,0.06)",
                                        border: m.role === "user" ? "none" : "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start">
                                <div className="text-white/60 text-xs inline-flex items-center gap-2 px-3 py-2">
                                    <Loader2 size={12} className="animate-spin" /> composing…
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="px-4 py-2 border-t border-white/10" style={{ backgroundColor: "#0d0d0d" }}>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="(optional) email — we'll notify you at drop"
                            className="w-full px-3 py-2 rounded-sm text-xs bg-black/40 border border-white/15 text-white/90 placeholder-white/30 focus:outline-none focus:border-[color:#c9a84c]"
                            data-testid="concierge-email"
                        />
                    </div>

                    <div className="flex items-center gap-2 p-3 border-t border-white/10" style={{ backgroundColor: "#0d0d0d" }}>
                        <input
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && !busy && send()}
                            placeholder={t("concierge.input_placeholder")}
                            className="flex-1 px-3 py-2.5 rounded-sm text-sm bg-black/40 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-[color:#c9a84c]"
                            disabled={busy}
                            data-testid="concierge-input"
                        />
                        <button
                            onClick={send}
                            disabled={busy || !text.trim()}
                            className="shrink-0 w-10 h-10 rounded-sm flex items-center justify-center text-forest-deep disabled:opacity-40"
                            style={{ backgroundColor: "#c9a84c" }}
                            data-testid="concierge-send"
                            aria-label="Send"
                        >
                            <Send size={14} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
